import { useMemo } from "react";
import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { ReportConfig, ReportDataFromJSON } from "lib/generated/admin-api";
import { graphql } from "lib/generated/app-service-gql";
import { ReportConfigQuery } from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";

const REPORT_CONFIG_QUERY = graphql(`
  query reportConfig($id: String!, $pdfToken: String) {
    reportConfig(id: $id, pdfToken: $pdfToken) {
      id
      label
      companyId
      teamId
      creatorId
      reportType
      adminNotes
      publishedAt
      reportData
      isPublic
      description
      pdfUrl
      coverImageUrl
    }
  }
`);

export type ParsedReportConfig = ReportConfig & { id: string };

function parseReportConfig(
  data: NonNullable<ReportConfigQuery["reportConfig"]>,
): ParsedReportConfig {
  const reportData = ReportDataFromJSON(JSON.parse(data.reportData));

  return {
    id: data.id,
    label: data.label,
    companyId: data.companyId,
    teamId: data.teamId ?? undefined,
    creatorId: data.creatorId ?? undefined,
    reportType: data.reportType,
    adminNotes: data.adminNotes ?? undefined,
    publishedAt: data.publishedAt ? new Date(data.publishedAt) : undefined,
    reportData,
    isPublic: data.isPublic ?? undefined,
    description: data.description ?? undefined,
    pdfUrl: data.pdfUrl ?? undefined,
    coverImageUrl: data.coverImageUrl ?? undefined,
  };
}

export function useReportConfig(
  id: string,
  // Short-lived signed token that grants access to a private report's config,
  // used by the PDF export flow when rendering the print page without a session.
  pdfToken?: string,
  options?: UseQueryOptions<ReportConfigQuery, unknown, ReportConfigQuery, QueryKey>,
) {
  const result = useGraphQlQuery(
    ["reportConfig", id, pdfToken],
    REPORT_CONFIG_QUERY,
    [{ id, pdfToken }],
    options,
  );
  const rawData = result.data?.reportConfig;
  // useMemo avoids re-running JSON.parse / model conversion on every render
  const data = useMemo(() => (rawData ? parseReportConfig(rawData) : undefined), [rawData]);
  return { ...result, data };
}
